import { ReactNode } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import {
  boschBlue,
  boschGray,
  boschGreen,
  boschWhite,
} from '../../modules/theme/boschColors';

/**
 * Color schemes available for `QuickLinkCard`.
 *
 * Each value maps to a `linear-gradient` background. They mirror the
 * four palettes the original (hard-coded) `QuickStatsCards` shipped on
 * the home page — exposing them by name keeps the visual language of
 * the home page intact for content pages built via `/create`.
 *
 * Keep the keys in sync with the `iconName` enum on the Scaffolder
 * template (`catalog/templates/new-content-page/template.yaml`) so the
 * admin form's dropdown options match what the renderer accepts.
 */
export type QuickLinkCardColor =
  | 'blue-dark'
  | 'blue'
  | 'blue-light'
  | 'green-dark'
  | 'green'
  | 'gray-dark'
  | 'gray';

const GRADIENTS: Record<QuickLinkCardColor, string> = {
  'blue-dark': `linear-gradient(135deg, ${boschBlue[10]} 0%, ${boschBlue[25]} 100%)`,
  blue: `linear-gradient(135deg, ${boschBlue[40]} 0%, ${boschBlue[55]} 100%)`,
  'blue-light': `linear-gradient(135deg, ${boschBlue[55]} 0%, ${boschBlue[65]} 100%)`,
  'green-dark': `linear-gradient(135deg, ${boschGreen[30]} 0%, ${boschGreen[40]} 100%)`,
  green: `linear-gradient(135deg, ${boschGreen[40]} 0%, ${boschGreen[50]} 100%)`,
  'gray-dark': `linear-gradient(135deg, ${boschGray[25]} 0%, ${boschGray[15]} 100%)`,
  gray: `linear-gradient(135deg, ${boschGray[35]} 0%, ${boschGray[20]} 100%)`,
};

const useStyles = makeStyles(theme => ({
  card: {
    position: 'relative',
    borderRadius: 4,
    padding: theme.spacing(2),
    color: boschWhite,
    minHeight: 110,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: 'none',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  iconWrapper: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(1.5),
    opacity: 0.35,
    color: boschWhite,
  },
  title: {
    fontSize: '0.7rem',
    fontWeight: 600,
    opacity: 0.95,
    marginBottom: theme.spacing(0.25),
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: boschWhite,
  },
  primaryText: {
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: theme.spacing(0.25),
    color: boschWhite,
  },
  secondaryText: {
    fontSize: '0.75rem',
    opacity: 0.85,
    marginBottom: theme.spacing(1),
    color: boschWhite,
  },
  actionLink: {
    color: `${boschWhite} !important`,
    fontWeight: 600,
    fontSize: '0.75rem',
    textDecoration: 'none',
    alignSelf: 'flex-end',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      textDecoration: 'underline',
      color: `${boschWhite} !important`,
    },
  },
}));

export type QuickLinkCardProps = {
  title: string;
  primaryText: string;
  secondaryText: string;
  actionLabel: string;
  actionUrl: string;
  icon: ReactNode;
  color?: QuickLinkCardColor;
  external?: boolean;
};

/**
 * A single coloured "quick link" tile — the visual building block of
 * the home page grid (Confluence, Uptime Kuma, SonarQube, etc.) and
 * the most reusable widget on `ContentPage` entities.
 *
 * Why a standalone component (split from `QuickStatsCards`):
 *
 *   - The home page renders a fixed, curated grid of these tiles, so
 *     `QuickStatsCards` kept a hard-coded array. That was fine when the
 *     home page was the only consumer.
 *   - Content pages (registered via `/create > New portal page`)
 *     are data-driven: the admin picks tiles one at a time and feeds
 *     each one a `title/primaryText/.../color/icon` payload. The
 *     renderer (`DynamicContentPage`) needs to instantiate ONE tile
 *     with arbitrary props — exactly the API of this component.
 *   - Sharing the same primitive across both surfaces guarantees the
 *     home page and any admin-authored page look 100% identical (same
 *     gradients, same hover treatment, same typography), so the
 *     content pages feel like a first-class part of the app rather
 *     than a bolted-on CMS.
 *
 * Internal vs external links: when `external: true`, the `Link` opens
 * in a new tab with `rel="noopener noreferrer"` — same behaviour as
 * the old home-page implementation, kept for parity.
 */
export const QuickLinkCard = ({
  title,
  primaryText,
  secondaryText,
  actionLabel,
  actionUrl,
  icon,
  color = 'blue',
  external = false,
}: QuickLinkCardProps) => {
  const classes = useStyles();
  return (
    <div
      className={classes.card}
      style={{ background: GRADIENTS[color] ?? GRADIENTS.blue }}
    >
      <div>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.primaryText}>{primaryText}</Typography>
        <Typography className={classes.secondaryText}>
          {secondaryText}
        </Typography>
      </div>
      <Link
        to={actionUrl}
        className={classes.actionLink}
        {...(external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {actionLabel} →
      </Link>
      <div className={classes.iconWrapper}>{icon}</div>
    </div>
  );
};
